// **The trace → renderer adapter** (closing **D-156**).
//
// The review screen used to draw its own DOM dots because a batch trace is not a wire
// stream and the screen has no session. But the shipped PixiJS world renderer does not
// want a session — it wants a `WorldSnapshot`, a `Camera` and a `RenderInput`, all of them
// plain data. So the whole swap from dots to sprites is this one pure function plus a
// canvas host: **no renderer edit, no wire change, no second rendering model.**
//
// What the mapping has to reconcile, and how:
//
//   * **Facing.** The trace carries no facing byte. Neither does the wire (a constant 0
//     since D-063) — the renderer's `FacingDeriver` derives heading from successive
//     positions. Feeding it successive trace ticks gives the same result by the same path.
//   * **The queen.** She is `typeId: null` in a detail record, but the renderer draws the
//     own colony's queen from `RenderInput.ownHiveAnchor` as the `own-queen` anchor sprite
//     (the live wire carries no close-tier queen for the own colony, D-091). She is
//     therefore excluded from the bee set — emitting her as a bee too would draw two
//     queens on one hive.
//   * **Cells.** The trace records `hp` and `occupied`; the wire's `CellRecord` wants
//     `damaged` and `ruin`. `damaged` is `hp < 255` (the quantized fraction of the balance
//     cell HP), `ruin` is "this cell's colony is no longer alive", read off the record's
//     own per-colony economy rows. `hpBucket` (`combat-strikes-and-damage-flash`) is
//     re-derived proportionally from the trace's own 0..255 `hp` rather than carried — the
//     trace has no separate bucket field, only the same fraction `damaged` already reads.
//   * **Blob ids.** The trace clusters on its own 8×8 grid; the wire packs `(colony,
//     supercell)` into one id. The id is re-packed in the CLIENT decoder's space so
//     `unpackBlobId` recovers the colony — identity only, since geometry rides the
//     centroid.
//   * **Season.** A trace manifest carries no season split, so it is derived from the
//     run's own progress against the boundaries the `SeasonClock` already displays —
//     imported, never restated, so the ground and the clock can never disagree.
//
// Truncation is always `false` in both flags: a trace record is the whole tick by
// construction, never a viewport-capped payload. That matters — `truncated` is one arm of
// the renderer's degradation trigger, and reporting a cap that did not happen would drop
// the review screen into `reduced` animation for no reason.

import type { ReviewTrace } from '../../net/rounds-review-source';
import {
  dequantizePosition,
  STATE_ID_NONE,
  type CoarseSample,
  type DetailRecord,
} from '../../net/rounds-trace';
import type { WorldSnapshot } from '../../net/wire/connection';
import type { BeeRecord, BlobRecord, CellRecord, PatchRecord } from '../../net/wire/messages';
import { ActionClass, Season, Tier } from '../../net/wire/messages';
import { supercellsPerAxis } from '../../canvas/blob-colony';
import type { RenderInput } from '../../canvas/render-input';
import type { WideColonyContext } from '../../canvas/renderer';
import { SEASON_BANDS } from '../components/hud/SeasonClock';

/** The u8 ceiling every quantized trace fraction is expressed against. A cell at exactly
 * this value is at full HP — anything below it is damaged. */
const FULL_FRACTION = 255;

/** The wire's hp bucket domain (`combat-strikes-and-damage-flash`): 0..31, five bits. */
const HP_BUCKET_MAX = 31;

/**
 * Approximates the wire's raw 0..31 hp bucket from the trace's already-quantized 0..255 `hp`
 * fraction — the same proportional mapping the server's real quantizer performs from continuous
 * hp, just re-derived from a coarser source (the trace lost precision to 8 bits before this
 * adapter ever sees it, so this is not byte-exact with a live wire bucket for the same cell).
 * Kept consistent with `damaged` above: any strictly positive `hp` still yields a strictly
 * positive bucket (never 0), mirroring the wire quantizer's own "0 is unreachable for a live
 * cell" rule, so an intact cell never misreads as empty.
 */
function hpBucketFromFraction(hp: number): number {
  if (hp <= 0) return 0;
  return Math.max(1, Math.round((hp / FULL_FRACTION) * HP_BUCKET_MAX));
}

/** The season whose band `progress` (0..1 through the run) falls in — the same boundaries
 * the `SeasonClock` paints, read from the same table so the ground tint and the clock can
 * never disagree about which season it is. */
export function seasonForProgress(progress: number): Season {
  const clamped = Math.min(1, Math.max(0, progress));
  const index = SEASON_BANDS.findIndex((band) => clamped <= band.to);
  return (index === -1 ? SEASON_BANDS.length - 1 : index) as Season;
}

function worldPoint(
  position: readonly [number, number],
  fieldSize: number,
): [number, number] {
  return [
    dequantizePosition(position[0], fieldSize),
    dequantizePosition(position[1], fieldSize),
  ];
}

/** Whether `colony` is still alive at this tick, per the record's own economy rows. A
 * colony the record does not mention is treated as alive — absence is not death. */
function colonyAlive(detail: DetailRecord, colony: number): boolean {
  return detail.colonies.find((c) => c.colony === colony)?.alive ?? true;
}

/**
 * Each labelled bee's B++ state name, keyed by bee id — the rounds-only `RenderInput` channel the
 * state-label layer reads (design **E5**).
 *
 * Ids resolve against the trace's own preamble table; a bee carrying the no-state sentinel simply
 * has **no entry**, which is what keeps "a bot's bee" distinguishable from "a scripted bee whose
 * name we could not resolve". The decoder has already refused any id that is neither the sentinel
 * nor a valid index as a corrupt trace, so the `undefined` guard below is unreachable through the
 * shipped path — it is here so this function stays total over a hand-built record rather than
 * putting `undefined` into a map typed as holding strings.
 */
function beeStateNamesFor(
  detail: DetailRecord | null,
  stateNames: readonly string[],
): ReadonlyMap<number, string> {
  const named = new Map<number, string>();
  if (detail === null) {
    return named;
  }
  for (const bee of detail.bees) {
    if (bee.stateId === STATE_ID_NONE) {
      continue;
    }
    const name = stateNames[bee.stateId];
    if (name !== undefined) {
      named.set(bee.id, name);
    }
  }
  return named;
}

function closeWorld(detail: DetailRecord, fieldSize: number, tick: number): WorldSnapshot {
  const bees = new Map<number, BeeRecord>();
  for (const bee of detail.bees) {
    if (bee.typeId === null) {
      continue; // the queen: drawn from the hive anchor, never as a bee (D-091)
    }
    bees.set(bee.id, {
      id: bee.id,
      position: worldPoint(bee.position, fieldSize),
      // No wire facing byte exists in either transport; the renderer derives heading.
      facing: 0,
      // `ActivityClass` and `ActionClass` are the same four values in the same order.
      action: bee.activity as unknown as ActionClass,
      gathering: bee.gathering,
      colonyId: bee.colony,
      typeId: bee.typeId,
      health: bee.health,
      nectar: bee.nectar,
    });
  }

  const patches = new Map<number, PatchRecord>();
  for (const patch of detail.patches) {
    patches.set(patch.id, {
      id: patch.id,
      position: worldPoint(patch.position, fieldSize),
      pool: patch.pool,
    });
  }

  const cells = new Map<number, CellRecord>();
  for (const cell of detail.cells) {
    cells.set(cell.id, {
      id: cell.id,
      position: worldPoint(cell.position, fieldSize),
      occupied: cell.occupied,
      damaged: cell.hp < FULL_FRACTION,
      ruin: !colonyAlive(detail, cell.colony),
      hpBucket: hpBucketFromFraction(cell.hp),
    });
  }

  return {
    tier: Tier.Close,
    tick,
    seq: BigInt(tick),
    bees,
    blobs: new Map(),
    queens: new Map(),
    patches,
    ruins: new Map(),
    // The trace records no dances (they are an action projection the writer does not
    // capture), so the overlay stays empty rather than inventing arrows.
    dances: new Map(),
    danceRadius: 0,
    cells,
    cellsTruncated: false,
    truncated: false,
    totalCount: bees.size,
  };
}

function wideWorld(coarse: CoarseSample, fieldSize: number, tick: number): WorldSnapshot {
  const perAxis = supercellsPerAxis(fieldSize);
  const blobs = new Map<number, BlobRecord>();
  for (const cluster of coarse.clusters) {
    // `colony * perAxis² + (sy * perAxis + sx)` — the exact shape `unpackBlobId` inverts.
    const blobId =
      cluster.colony * perAxis * perAxis + (cluster.cell[1] * perAxis + cluster.cell[0]);
    blobs.set(blobId, {
      blobId,
      centroid: [cluster.centroid[0], cluster.centroid[1]],
      count: cluster.members,
      dominantAction: cluster.activity as unknown as ActionClass,
    });
  }
  return {
    tier: Tier.Wide,
    tick,
    seq: BigInt(tick),
    bees: new Map(),
    blobs,
    queens: new Map(),
    patches: new Map(),
    ruins: new Map(),
    dances: new Map(),
    danceRadius: 0,
    cells: new Map(),
    cellsTruncated: false,
    truncated: false,
    totalCount: 0,
  };
}

/**
 * The `WorldSnapshot` for `tick`: the **detail** tier when its range is resident, the
 * **coarse** tier otherwise, `null` when neither has anything. The same
 * detail-else-coarse rule the screen's tier indicator reports, so the picture and the
 * indicator can never disagree.
 */
export function reviewWorldSnapshot(trace: ReviewTrace, tick: number): WorldSnapshot | null {
  const detail = trace.detailAt(tick);
  if (detail !== null) {
    return closeWorld(detail, trace.fieldSize, tick);
  }
  const coarse = trace.coarseAt(tick);
  return coarse === null ? null : wideWorld(coarse, trace.fieldSize, tick);
}

/**
 * The render-input context for `tick`: the hive anchor (which draws the hive tile and the
 * own queen), the viewer's own colony (which gives its bees their type colours rather
 * than enemy colours), the derived season, and the **playhead** — the field the renderer
 * already has for exactly this, so a backward scrub resets interpolation history instead
 * of tweening every bee across the field — and the **rounds-only `beeStateNames` channel**
 * (WAG-36, design **E5**), each labelled bee's B++ state name resolved against the trace's own
 * preamble table. That channel rides here rather than on the wire's `BeeRecord` precisely so the
 * property this module's header claims still holds: adapting a trace frame costs no wire change
 * and no second rendering model.
 *
 * The epochs stay 0: a trace has no connection and no sequence gaps, so there is no
 * discontinuity to signal. Scrubbing is signalled by the playhead, which is what it is for.
 *
 * `pickedBeeId` rides straight onto `RenderInput.selectedBeeId` (bee-selection-outline): the
 * screen's WAG-36 pick (which drives the persistent state-label, via `ReviewFrame.setPickedBee`)
 * is the same bee the tap-to-outline silhouette draws for, so this adapter is the one seam both
 * the sandbox and graded-review screens share. `null` (the default) draws no outline, matching
 * "nothing picked".
 */
export function reviewRenderInput(
  trace: ReviewTrace,
  tick: number,
  hive: readonly [number, number] | null,
  colony: number,
  pickedBeeId: number | null = null,
): RenderInput {
  const detail = trace.detailAt(tick);
  const alive = detail === null ? true : colonyAlive(detail, colony);
  const span = trace.terminalTick - trace.firstTick;
  const progress = span > 0 ? (tick - trace.firstTick) / span : 0;
  return {
    season: seasonForProgress(progress),
    ownHiveAnchor: hive,
    ownColonyLifecycle: alive ? 'live' : 'eliminated',
    ownColonyId: colony,
    playhead: tick,
    connectionEpoch: 0,
    streamEpoch: 0,
    // **Always present on this path, even when empty** (design **E5**): a bot-only run interns no
    // state names and labels nothing, and that is a different fact from a live frame, which omits
    // the channel entirely. The renderer branches on exactly that difference.
    beeStateNames: beeStateNamesFor(detail, trace.stateNames ?? []),
    selectedBeeId: pickedBeeId,
  };
}

/** The wide-tier colouring context: the run's colony roster, so a blob takes its colony's
 * colour instead of a neutral one. Read from whichever tier answers for `tick`. */
export function reviewWideContext(trace: ReviewTrace, tick: number): WideColonyContext {
  const detail = trace.detailAt(tick);
  const colonyIds =
    detail !== null
      ? detail.colonies.map((c) => c.colony)
      : (trace.coarseAt(tick)?.colonies.map((c) => c.colony) ?? []);
  return { fieldSize: trace.fieldSize, colonyIds };
}
